import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {useEffect, useRef, useState, type ReactElement, type RefObject} from 'react'
import {useTaskActionItems, type TaskActionItem, type UseTaskActionItemsProps} from '../hooks/use-task-action-items'
import {TaskPinErrorDialog} from './TaskPinErrorDialog'

export interface TaskActionsProps extends UseTaskActionItemsProps {
  /** aria-label for the kebab button (default: "Task actions") */
  ariaLabel?: string
  /** Alignment of the overlay (default: undefined / start) */
  overlayAlign?: 'start' | 'end'
  /** Ref for the ActionMenu anchor button */
  anchorRef?: RefObject<HTMLButtonElement | null>
  /** Custom anchor for surfaces that need a different trailing action */
  anchor?: ReactElement
  /** data-testid for the kebab button */
  testId?: string
  /** Called when the kebab button is clicked (e.g. for analytics) */
  onOpen?: () => void
  onOpenChange?: (open: boolean) => void
  /** Additional items to append to the action menu */
  extraItems?: TaskActionItem[]
}

/**
 * Kebab ActionMenu for task actions. Renders the full menu: anchor button + overlay + action list.
 * Returns null when no actions are available.
 */
export function TaskActions({
  ariaLabel = 'Task actions',
  overlayAlign,
  anchorRef,
  anchor,
  testId,
  onOpen,
  onOpenChange,
  extraItems,
  ...actionProps
}: TaskActionsProps) {
  const fallbackAnchorRef = useRef<HTMLButtonElement>(null)
  const effectiveAnchorRef = anchorRef ?? fallbackAnchorRef
  const [open, setOpen] = useState(false)
  const {items, hasAnyAction, pinError, dismissPinError} = useTaskActionItems(actionProps)
  const allItems = extraItems?.length ? [...items, ...extraItems] : items

  if (pinError && open) setOpen(false)

  useEffect(() => {
    if (pinError) onOpenChange?.(false)
  }, [pinError, onOpenChange])

  if (!hasAnyAction && !extraItems?.length) return null

  return (
    <>
      <TaskPinErrorDialog error={pinError} onClose={dismissPinError} returnFocusRef={effectiveAnchorRef} />
      <ActionMenu
        anchorRef={effectiveAnchorRef}
        open={open && !pinError}
        onOpenChange={nextOpen => {
          setOpen(nextOpen)
          onOpenChange?.(nextOpen)
        }}
      >
        <ActionMenu.Anchor>
          {anchor ?? (
            <IconButton
              icon={KebabHorizontalIcon}
              variant="invisible"
              aria-label={ariaLabel}
              onClick={onOpen}
              {...(testId ? {'data-testid': testId} : {})}
            />
          )}
        </ActionMenu.Anchor>
        <ActionMenu.Overlay align={overlayAlign}>
          <ActionList>
            {allItems.map(item =>
              item.href ? (
                <ActionList.LinkItem key={item.key} href={item.href} onClick={() => item.onSelect()}>
                  <ActionList.LeadingVisual>
                    <item.icon />
                  </ActionList.LeadingVisual>
                  {item.label}
                </ActionList.LinkItem>
              ) : (
                <ActionList.Item
                  key={item.key}
                  variant={item.variant}
                  onSelect={item.onSelect}
                  disabled={item.disabled}
                  loading={item.loading}
                >
                  <ActionList.LeadingVisual>
                    <item.icon />
                  </ActionList.LeadingVisual>
                  {item.label}
                </ActionList.Item>
              ),
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}
